import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Target, PoundSterling, Trophy, ListChecks, CalendarDays, Bell, Megaphone, Link2, UserPlus, SlidersHorizontal, ArrowUp, ArrowDown, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ws, gbp, fmtDate, empName } from "./api";
import { useWorkspace } from "./WorkspaceContext";
import { Panel, Empty, Loading } from "./ui";

const LABELS = {
  kpis: "Key metrics", sales_overview: "Sales overview", my_tasks: "My tasks", upcoming_meetings: "Upcoming meetings",
  notifications: "Notifications", announcements: "Announcements", quick_links: "Quick links", new_hires: "New hires",
};
const STAGE_COLORS = { lead: "#94A3B8", qualified: "#6366F1", proposal: "#0EA5E9", negotiation: "#E8A33D", won: "#10B981", lost: "#EF4444" };

const Kpi = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-white rounded-xl border border-slate-200/80 p-4 sm:p-5" data-testid={testid}>
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-500 font-medium"><Icon className="h-3.5 w-3.5" />{label}</div>
    <div className="font-heading text-2xl font-semibold text-[#0B1530] mt-2">{value}</div>
  </div>
);

const Kpis = ({ k }) => (
  <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 lg:col-span-2" data-testid="widget-kpis">
    <Kpi icon={Users} label="Contacts" value={k.contacts} testid="kpi-contacts" />
    <Kpi icon={Target} label="Open deals" value={k.open_deals} testid="kpi-open-deals" />
    <Kpi icon={PoundSterling} label="Pipeline value" value={gbp(k.pipeline_value)} testid="kpi-pipeline-value" />
    <Kpi icon={Trophy} label="Won this month" value={gbp(k.won_this_month)} testid="kpi-won-month" />
    <Kpi icon={ListChecks} label="Tasks due (7d)" value={k.tasks_due} testid="kpi-tasks-due" />
  </div>
);

const SalesOverview = ({ s }) => {
  const entries = Object.entries(s);
  const max = Math.max(1, ...entries.map(([, v]) => v.value));
  return (
    <Panel title="Sales overview" testid="widget-sales_overview" action={<Link to="/crm-app/sales" className="text-xs text-slate-500 hover:text-[#0B1530]">Open pipeline</Link>}>
      {entries.length === 0 ? <Empty icon={Target} title="No deals yet" hint="Create your first deal in CRM & Sales." /> : (
        <div className="space-y-3">
          {entries.map(([stage, v]) => (
            <div key={stage} data-testid={`sales-stage-${stage}`}>
              <div className="flex justify-between text-xs mb-1"><span className="capitalize text-slate-600">{stage} · {v.count}</span><span className="font-medium text-slate-800">{gbp(v.value)}</span></div>
              <div className="h-2 rounded-full bg-slate-100"><div className="h-full rounded-full" style={{ width: `${(v.value / max) * 100}%`, background: STAGE_COLORS[stage] || "#64748B" }} /></div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
};

const ListWidget = ({ id, title, icon, items, empty, render, link }) => (
  <Panel title={title} testid={`widget-${id}`} action={link && <Link to={link} className="text-xs text-slate-500 hover:text-[#0B1530]">View all</Link>}>
    {items.length === 0 ? <Empty icon={icon} title={empty} /> : <ul className="divide-y divide-slate-100 -my-2">{items.map(render)}</ul>}
  </Panel>
);

const Customise = ({ open, onOpenChange, widgets, onSave }) => {
  const [list, setList] = useState(widgets);
  useEffect(() => setList(widgets), [widgets, open]);
  const move = (i, d) => { const n = [...list]; [n[i], n[i + d]] = [n[i + d], n[i]]; setList(n); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} data-testid="customise-dialog">
        <DialogHeader><DialogTitle>Customise your dashboard</DialogTitle></DialogHeader>
        <ul className="space-y-1.5">
          {list.map((w, i) => (
            <li key={w.id} className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2" data-testid={`widget-pref-${w.id}`}>
              <Switch checked={w.visible} onCheckedChange={(v) => setList(list.map((x) => (x.id === w.id ? { ...x, visible: v } : x)))} data-testid={`widget-toggle-${w.id}`} />
              <span className="text-sm flex-1">{LABELS[w.id]}</span>
              <button disabled={i === 0} onClick={() => move(i, -1)} className="p-1 disabled:opacity-30" data-testid={`widget-up-${w.id}`}><ArrowUp className="h-4 w-4" /></button>
              <button disabled={i === list.length - 1} onClick={() => move(i, 1)} className="p-1 disabled:opacity-30" data-testid={`widget-down-${w.id}`}><ArrowDown className="h-4 w-4" /></button>
            </li>
          ))}
        </ul>
        <Button onClick={() => onSave(list)} className="bg-[#0B1530] hover:bg-[#1a2650]" data-testid="customise-save-btn">Save layout</Button>
      </DialogContent>
    </Dialog>
  );
};

export default function HomeDashboard() {
  const { me } = useWorkspace();
  const [d, setD] = useState(null);
  const [widgets, setWidgets] = useState([]);
  const [notes, setNotes] = useState([]);
  const [customise, setCustomise] = useState(false);
  useEffect(() => {
    ws("/crm/workspace/dashboard").then(setD).catch((e) => toast.error(e.message));
    ws("/crm/workspace/prefs").then((p) => setWidgets(p.dashboard_widgets)).catch(() => {});
    ws("/crm/workspace/notifications").then((n) => setNotes(n.notifications.slice(0, 5))).catch(() => {});
  }, []);
  const save = async (list) => {
    try { const p = await ws("/crm/workspace/prefs", { method: "PUT", body: { dashboard_widgets: list } }); setWidgets(p.dashboard_widgets); setCustomise(false); toast.success("Dashboard saved"); }
    catch (e) { toast.error(e.message); }
  };
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  if (!d) return <Loading />;
  const W = {
    kpis: <Kpis k={d.kpis} />,
    sales_overview: <SalesOverview s={d.sales_by_stage} />,
    my_tasks: <ListWidget id="my_tasks" title="My tasks" icon={ListChecks} items={d.my_tasks} empty="No open tasks" link="/crm-app/tasks"
      render={(t) => <li key={t.task_id} className="py-2.5 flex justify-between gap-3 text-sm"><span className="truncate">{t.title}</span><span className="text-xs text-slate-500 shrink-0">{fmtDate(t.due_at)}</span></li>} />,
    upcoming_meetings: <ListWidget id="upcoming_meetings" title="Upcoming meetings" icon={CalendarDays} items={d.upcoming_meetings} empty="No meetings scheduled" link="/crm-app/calendar"
      render={(t) => <li key={t.task_id} className="py-2.5 flex justify-between gap-3 text-sm"><span className="truncate">{t.title}</span><span className="text-xs text-slate-500 shrink-0">{fmtDate(t.due_at, true)}</span></li>} />,
    notifications: <ListWidget id="notifications" title="Notifications" icon={Bell} items={notes} empty="You're all caught up"
      render={(n) => <li key={n.notification_id} className="py-2.5 text-sm"><p className="truncate">{n.title}</p><p className="text-[11px] text-slate-400">{fmtDate(n.created_at, true)}</p></li>} />,
    announcements: <ListWidget id="announcements" title="Announcements" icon={Megaphone} items={d.announcements} empty="No announcements yet" link="/crm-app/organization/announcements"
      render={(a) => <li key={a.announcement_id} className="py-2.5"><p className="text-sm font-medium">{a.title}</p><p className="text-xs text-slate-500 line-clamp-2">{a.body}</p></li>} />,
    quick_links: <ListWidget id="quick_links" title="Quick links" icon={Link2} items={d.quick_links} empty="No quick links added" link="/crm-app/organization/quick-links"
      render={(l) => <li key={l.link_id} className="py-2.5"><a href={l.url} target="_blank" rel="noreferrer" className="text-sm text-[#0B1530] hover:underline inline-flex items-center gap-1.5">{l.title}<ExternalLink className="h-3 w-3" /></a></li>} />,
    new_hires: <ListWidget id="new_hires" title="New hires (30 days)" icon={UserPlus} items={d.new_hires} empty="No new joiners recently" link="/crm-app/organization/new-hires"
      render={(e) => <li key={e.employee_id} className="py-2.5 flex justify-between text-sm"><span>{empName(e)}</span><span className="text-xs text-slate-500">{e.designation || e.employee_id}</span></li>} />,
  };
  return (
    <div data-testid="home-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#B87A1F] font-semibold">{new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-[#0B1530] mt-1" data-testid="home-greeting">{greet}, {me?.name?.split(" ")[0]}</h1>
          <p className="text-sm text-slate-500 mt-1">Here's what's happening at {me?.organization?.business_name}.</p>
        </div>
        <Button variant="outline" onClick={() => setCustomise(true)} data-testid="customise-dashboard-btn"><SlidersHorizontal className="h-4 w-4 mr-2" />Customise</Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {widgets.filter((w) => w.visible).map((w) => <div key={w.id} className={w.id === "kpis" ? "lg:col-span-2" : ""}>{W[w.id]}</div>)}
      </div>
      {widgets.length > 0 && widgets.every((w) => !w.visible) && <Empty title="All widgets are hidden" hint="Use Customise to bring some back." />}
      <Customise open={customise} onOpenChange={setCustomise} widgets={widgets} onSave={save} />
    </div>
  );
}
